import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEntityDetails, useTopicPage } from '../hooks/useApi';
import { useState } from 'react';

// Add CSS for highlighting
const markStyle = {
  backgroundColor: '#fef3c7',
  color: '#92400e',
  padding: '0.125rem 0.25rem',
  borderRadius: '0.25rem',
  margin: '0 0.125rem'
};

// Add this truncation function near the top of the file
function truncateHtmlAroundKeyword(html, keyword, radius = 2) {
  if (!html || !keyword) return html;

  // Split HTML into sections while preserving important structural tags
  const sections = html.split(/(<\/?(?:p|h[1-6]|ul|ol|li|div|section|article|header|main)(?:\s[^>]*)?\/?>)/).filter(Boolean);

  // Find sections containing the keyword
  const relevantSections = [];
  let foundIndices = [];

  // Helper function to highlight keyword in text
  const highlightKeyword = (text) => {
    // Split text into text nodes and HTML tags
    const parts = text.split(/(<[^>]*>)/);
    return parts.map(part => {
      // Skip HTML tags
      if (part.startsWith('<')) {
        return part;
      }
      // Highlight keyword in text nodes
      const regex = new RegExp(`(${keyword})`, 'gi');
      return part.replace(regex, `<mark style="background-color: #fef3c7; color: #92400e; padding: 0.125rem 0.25rem; border-radius: 0.25rem; margin: 0 0.125rem">$1</mark>`);
    }).join('');
  };

  // First pass: find all sections with the keyword
  sections.forEach((section, index) => {
    if (section.toLowerCase().includes(keyword.toLowerCase())) {
      foundIndices.push(index);
    }
  });

  // Second pass: collect relevant sections with context
  foundIndices.forEach(index => {
    // Find the nearest heading before this section
    let headerIndex = index;
    while (headerIndex > 0) {
      if (sections[headerIndex].match(/<h[1-6][^>]*>/)) {
        // Add the header
        if (!relevantSections.includes(sections[headerIndex])) {
          relevantSections.push(sections[headerIndex]);
        }
        break;
      }
      headerIndex--;
    }

    // Add surrounding sections based on radius
    for (let i = Math.max(0, index - radius); i <= Math.min(sections.length - 1, index + radius); i++) {
      const section = sections[i];

      // Skip empty or purely structural sections
      if (section.trim().length === 0 || section.match(/^<\/?(?:div|section|article|main)>/)) {
        continue;
      }

      // Only highlight the section containing the keyword
      const processedSection = i === index ? highlightKeyword(section) : section;

      if (!relevantSections.includes(processedSection)) {
        relevantSections.push(processedSection);
      }
    }
  });

  if (foundIndices.length === 0) return html;

  // Join relevant sections and add ellipsis
  return `...${relevantSections.join('')}...`;
}

function EntityDetails() {
  const { type, entity } = useParams();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  console.log('EntityDetails: Component rendering with params:', { type, entity });

  const { data: entityData, isLoading: entityLoading, error: entityError } = useEntityDetails(type, entity, currentPage, itemsPerPage);

  // Update the group extraction logic
  const firstMention = entityData?.entityData?.[0];
  const entityInfo = entityData?.rawEntitySummary?.[0];
  const displayText = entityInfo?.readable_form || firstMention?.normalized_entity_text || entity;
  const group = {
    group: entityInfo?.group,
    sub_group: entityInfo?.sub_group
  };

  console.log('EntityDetails: Entity Info:', {
    rawEntitySummary: entityData?.rawEntitySummary,
    entityInfo,
    displayText,
    group
  });

  // Move useTopicPage here with other hooks
  const { data: topicPageData, isLoading: topicLoading } = useTopicPage(
    displayText,
    group?.group
  );

  console.log('EntityDetails: Topic page data:', {
    hasTopicPage: !!topicPageData,
    topicLoading
  });

  if (entityLoading || topicLoading) {
    console.log('EntityDetails: Loading state', { entityLoading, topicLoading });
    return <div className="animate-pulse">Loading...</div>;
  }

  if (entityError) {
    console.error('EntityDetails: Error state:', entityError);
    return <div className="text-red-500">Error loading entity details: {entityError.message}</div>;
  }

  if (!entityData || !entityData.entityData.length) {
    console.log('EntityDetails: No entity data found');
    return <div className="text-red-500">Entity not found</div>;
  }

  console.log('EntityDetails: Raw entity data:', {
    entityDataLength: entityData.entityData.length,
    firstItem: entityData.entityData[0],
    allItems: entityData.entityData
  });

  const mentions = entityData.entityData.map(item => {
    console.log('Processing item:', {
      authors: item.authors,
      hasCase: !!item.case_study,
      firstAuthor: Array.from(item.authors)[0]
    });

    return {
      author: Array.from(item.authors)[0],
      context: item.case_study
        ? truncateHtmlAroundKeyword(item.case_study, displayText)
        : '<div class="text-gray-500 italic">Case study content not available</div>'
    };
  });

  console.log('EntityDetails: Processed mentions:', {
    totalMentions: mentions.length,
    allMentions: mentions,
    mentionsWithContext: mentions.filter(m => m.context !== '<div class="text-gray-500 italic">Case study content not available</div>').length
  });

  const topicPage = topicPageData?.[0];

  // Get pagination info from the API response
  const { totalPages } = entityData.pagination;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/type/${encodeURIComponent(type)}`)}
          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to {type}
        </button>
      </div>

      <div className="mb-6">
        <h2 className="text-2xl font-semibold">{displayText}</h2>
        <p className="text-gray-600">Type: <Link to={`/type/${encodeURIComponent(type)}`} className="text-blue-600 hover:text-blue-800">{type}</Link></p>
        {group?.group && (
          <p className="text-gray-600">
            Group: <Link to={`/type/${encodeURIComponent(type)}#${encodeURIComponent(group.group)}`} className="text-blue-600 hover:text-blue-800">{group.group}</Link>
          </p>
        )}
        {group?.sub_group && (
          <p className="text-gray-600">
            Sub-group: <Link to={`/type/${encodeURIComponent(type)}#${encodeURIComponent(group.sub_group)}`} className="text-blue-600 hover:text-blue-800">{group.sub_group}</Link>
          </p>
        )}
        <p className="text-gray-600">Found in {entityData.totalMentions} case stud{entityData.totalMentions !== 1 ? 'ies' : 'y'}</p>
      </div>

      {topicPage && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Topic Summary</h3>
          <style>
            {`
              .markdown-content hr {
                margin: 1rem 0;
                height: 1px;
                background-color: #e5e7eb;
                border: none;
              }
            `}
          </style>
          <div
            className="prose max-w-none markdown-content"
            dangerouslySetInnerHTML={{ __html: topicPage.content_html || topicPage.content }}
          />
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold">Mentions in Case Studies</h3>
          <span className="text-sm text-gray-500">
            Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, entityData.totalMentions)} of {entityData.totalMentions}
          </span>
        </div>
        <style>
          {`
            .case-study-content {
              font-size: 0.875rem;
              line-height: 1.5;
            }
            .case-study-content p {
              margin-bottom: 0.75rem;
            }
            .case-study-content ul, .case-study-content ol {
              margin-left: 1.5rem;
              margin-bottom: 0.75rem;
            }
            .case-study-content li {
              margin-bottom: 0.25rem;
            }
            .case-study-content h1, .case-study-content h2, .case-study-content h3,
            .case-study-content h4, .case-study-content h5, .case-study-content h6 {
              font-weight: 600;
              margin-top: 1rem;
              margin-bottom: 0.5rem;
            }
            .case-study-content mark {
              background-color: #fef3c7;
              color: #92400e;
              padding: 0.125rem 0.25rem;
              border-radius: 0.25rem;
              margin: 0 0.125rem;
            }
          `}
        </style>
        <div className="border rounded-lg overflow-hidden">
          {mentions.map((mention, index) => (
            <div
              key={index}
              className={`border-b last:border-b-0 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
            >
              <div className="px-4 py-2 flex items-center justify-between border-b border-gray-100">
                <Link
                  to={`/case-study/${encodeURIComponent(mention.author)}`}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  {mention.author}'s Case Study
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </Link>
              </div>
              <div
                className="px-4 py-2 case-study-content"
                dangerouslySetInnerHTML={{ __html: mention.context }}
              />
            </div>
          ))}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-4 flex flex-wrap justify-center items-center gap-2">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="min-w-[32px] h-8 px-2 rounded border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed hidden sm:block"
                aria-label="First page"
              >
                «
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="min-w-[32px] h-8 px-2 rounded border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                aria-label="Previous page"
              >
                <span className="sm:hidden">Prev</span>
                <span className="hidden sm:block">‹</span>
              </button>
            </div>

            <div className="flex items-center gap-1">
              {(() => {
                const pageButtons = [];
                const showEllipsis = (position) => (
                  <span key={`ellipsis-${position}`} className="hidden sm:block text-gray-400">...</span>
                );

                // Show first page, last page, current page, and pages around current
                for (let i = 1; i <= totalPages; i++) {
                  if (
                    i === 1 ||
                    i === totalPages ||
                    (i >= currentPage - 1 && i <= currentPage + 1) ||
                    (i === 2 && currentPage <= 4) ||
                    (i === totalPages - 1 && currentPage >= totalPages - 3)
                  ) {
                    pageButtons.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`min-w-[32px] h-8 px-2 rounded border ${
                          currentPage === i
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'hover:bg-gray-50 text-gray-700'
                        } hidden sm:block`}
                      >
                        {i}
                      </button>
                    );
                  } else if (
                    (i === currentPage - 2 && currentPage > 4) ||
                    (i === currentPage + 2 && currentPage < totalPages - 3)
                  ) {
                    pageButtons.push(showEllipsis(i));
                  }
                }

                return (
                  <>
                    {pageButtons}
                    {/* Mobile page indicator */}
                    <span className="sm:hidden text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </span>
                  </>
                );
              })()}
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="min-w-[32px] h-8 px-2 rounded border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                aria-label="Next page"
              >
                <span className="sm:hidden">Next</span>
                <span className="hidden sm:block">›</span>
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="min-w-[32px] h-8 px-2 rounded border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed hidden sm:block"
                aria-label="Last page"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EntityDetails;